import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  MessageCircle,
  Heart,
  Leaf,
  Users,
  Sparkles,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { WHATSAPP_LINK, api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const helpItems = [
  { emoji: "😰", label: "Ansiedade" },
  { emoji: "💭", label: "Angústia" },
  { emoji: "🌧️", label: "Tristeza persistente" },
  { emoji: "🪞", label: "Baixa autoestima" },
  { emoji: "💔", label: "Dificuldades nos relacionamentos" },
  { emoji: "🫧", label: "Sofrimento emocional" },
  { emoji: "🏠", label: "Conflitos familiares" },
  { emoji: "🎒", label: "Adolescência" },
  { emoji: "🌫️", label: "Sentimento de vazio" },
  { emoji: "🦋", label: "Processos de autoconhecimento" },
];

const faqs = [
  {
    q: "Como funciona a primeira sessão?",
    a: "A primeira conversa é um encontro acolhedor, sem compromisso. Nesse espaço, escuto você com cuidado, compreendo o que está te trazendo até a análise e explico como costumamos trabalhar a partir dali.",
  },
  {
    q: "Quanto tempo dura uma sessão?",
    a: "Cada sessão tem em média 50 minutos. A frequência semanal costuma ser de uma a duas sessões, conforme construímos juntos no início do processo.",
  },
  {
    q: "Preciso ter um problema específico para iniciar análise?",
    a: "Não. A análise também é um espaço de autoconhecimento. Muitas pessoas procuram porque sentem um incômodo difícil de nomear, e justamente esse é um ponto valioso de partida.",
  },
  {
    q: "O atendimento é sigiloso?",
    a: "Absolutamente. O sigilo é uma das bases éticas do trabalho psicanalítico. Tudo o que é compartilhado em sessão permanece protegido.",
  },
  {
    q: "Atende adolescentes?",
    a: "Sim, atendo adolescentes em ambiente acolhedor e respeitoso, com participação cuidadosa dos pais quando necessária.",
  },
  {
    q: "O atendimento é online ou presencial?",
    a: "Os atendimentos são realizados online por videochamada, garantindo qualidade da escuta, privacidade e conforto.",
  },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((res) => setTestimonials(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-32 -right-40 w-[520px] h-[520px] rounded-full bg-[#B4C5E4]/25 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#8BA88E]/15 blur-3xl"
        />
        <div className="container-narrow relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center pt-6 pb-0 md:pt-10 md:pb-0 lg:pt-12 lg:pb-0">
          <div className="lg:col-span-7 stagger order-2 lg:order-1">
            <span className="text-eyebrow">Psicanálise Clínica · Atendimento Online</span>
            <h1 className="font-serif text-[2.25rem] sm:text-5xl lg:text-[4rem] xl:text-[4.25rem] leading-[1.1] tracking-tight text-[#2C352D] mt-5 sm:mt-6">
              Um espaço de <em className="italic font-light text-[#8BA88E]">escuta</em>,
              <br /> acolhimento e elaboração.
            </h1>
            <p className="mt-5 sm:mt-6 max-w-xl text-[#5B685C] text-base sm:text-[1.05rem] leading-relaxed font-light">
              Para quem busca compreender seu sofrimento, reorganizar sua história e construir novos caminhos
              — em um ambiente seguro, sigiloso e cuidadoso.
            </p>
            <div className="mt-7 sm:mt-8 flex flex-row flex-wrap items-center gap-3 sm:gap-4">
              <a
                data-testid="hero-whatsapp-btn"
                href={WHATSAPP_LINK("Olá Cristina, gostaria de agendar uma conversa.")}
                target="_blank"
                rel="noreferrer"
                className="btn-whatsapp"
              >
                <MessageCircle size={18} /> Agendar pelo WhatsApp
              </a>
              <Link data-testid="hero-about-link" to="/sobre" className="btn-outline">
                Conhecer Cristina <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative order-1 lg:order-2">
            <div className="relative">
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-8 top-12 rounded-full bg-gradient-to-br from-[#8BA88E]/15 to-[#B4C5E4]/15 blur-2xl"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_escuta-acolhimento/artifacts/sa4f7j67_foto%20profissional%201.png"
                alt="Cristina Moreira, psicanalista clínica"
                className="relative w-full h-[380px] sm:h-[480px] md:h-[560px] lg:h-[600px] object-contain object-bottom"
                data-testid="hero-portrait"
              />
              <div className="absolute -bottom-2 left-2 sm:-bottom-4 sm:-left-4 bg-white rounded-2xl px-5 py-3 sm:px-6 sm:py-4 soft-shadow border border-[#8BA88E]/15 z-10">
                <p className="text-[10px] sm:text-xs uppercase tracking-widest text-[#8BA88E] font-medium">Atendimento</p>
                <p className="font-serif text-lg sm:text-xl text-[#2C352D] mt-1">Online · Brasil & Exterior</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO / SOBRE TEASER */}
      <section className="bg-[#F5F5ED] py-8 sm:py-10 md:py-14 lg:py-16">
        <div className="container-narrow grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <img
              src="https://customer-assets.emergentagent.com/job_escuta-acolhimento/artifacts/f7xogrey_foto%20profissional%202gpt.png"
              alt="Cristina Moreira"
              className="w-full h-[380px] sm:h-[460px] md:h-[540px] object-contain object-bottom"
              data-testid="home-about-image"
            />
          </div>
          <div className="lg:col-span-7">
            <span className="text-eyebrow">Quem sou</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#2C352D] leading-tight">
              Sou Cristina Moreira,<br /> psicanalista clínica.
            </h2>
            <div className="mt-6 prose-soft">
              <p>
                Acredito que a escuta cuidadosa permite que cada sujeito encontre novos significados
                para sua história, suas dores e seus desejos. Meu trabalho oferece um espaço seguro
                para falar, refletir e elaborar conflitos emocionais.
              </p>
              <p>
                Atendo adultos e adolescentes em sessões online, com sigilo absoluto e dedicação ao
                tempo singular de cada pessoa.
              </p>
            </div>
            <Link data-testid="home-about-link" to="/sobre" className="btn-outline mt-8 inline-flex">
              Conhecer minha trajetória <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* COMO PODE AJUDAR */}
      <section className="section">
        <div className="container-narrow">
          <div className="max-w-2xl">
            <span className="text-eyebrow">Como a psicanálise pode ajudar</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#2C352D] leading-tight">
              Situações em que a análise pode oferecer acolhimento.
            </h2>
            <p className="mt-6 text-[#5B685C] text-lg leading-relaxed font-light">
              A psicanálise não trata apenas sintomas; ela escuta o sujeito por trás deles. A seguir, alguns
              momentos em que essa escuta pode ser especialmente valiosa.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {helpItems.map((item, i) => (
              <div
                key={item.label}
                data-testid={`help-item-${i}`}
                className="bg-white rounded-2xl border border-[#8BA88E]/15 px-6 py-5 flex items-center gap-4 soft-shadow hover-shadow transition-all duration-300"
              >
                <span className="h-11 w-11 rounded-full bg-[#8BA88E]/10 flex items-center justify-center text-2xl leading-none shrink-0" aria-hidden="true">
                  {item.emoji}
                </span>
                <span className="text-[#2C352D] font-light">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADOLESCENTES & ONLINE */}
      <section className="section bg-[#F5F5ED]">
        <div className="container-narrow grid lg:grid-cols-2 gap-10">
          <div className="bg-white rounded-3xl overflow-hidden soft-shadow border border-[#8BA88E]/10">
            <img
              src="https://customer-assets.emergentagent.com/job_escuta-acolhimento/artifacts/xb887edx_ChatGPT%20Image%2027%20de%20jun.%20de%202026%2C%2020_08_33.png"
              alt="Atendimento para adolescentes"
              className="w-full h-72 object-cover"
              data-testid="home-adolescentes-image"
            />
            <div className="p-8 md:p-10">
              <span className="text-eyebrow">Atendimento para adolescentes</span>
              <h3 className="font-serif text-3xl mt-4 text-[#2C352D]">
                Escuta cuidadosa em uma travessia singular.
              </h3>
              <p className="mt-4 text-[#5B685C] font-light leading-relaxed">
                A adolescência é tempo de transformação. Ofereço um espaço sigiloso, próprio do jovem, com
                participação cuidadosa dos pais quando necessário.
              </p>
              <Link data-testid="home-adolescentes-link" to="/adolescentes" className="mt-6 inline-flex items-center gap-2 text-[#8BA88E] hover:text-[#769379] font-medium">
                Saber mais <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="bg-white rounded-3xl overflow-hidden soft-shadow border border-[#8BA88E]/10">
            <img
              src="https://images.unsplash.com/photo-1588091209794-8aa1768e2937?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxsYXB0b3AlMjBjb2ZmZWUlMjBjYWxtJTIwZGVzayUyMG9ubGluZXxlbnwwfHx8fDE3ODI1ODg1MTd8MA&ixlib=rb-4.1.0&q=85"
              alt="Atendimento online"
              className="w-full h-72 object-cover"
              data-testid="home-online-image"
            />
            <div className="p-8 md:p-10">
              <span className="text-eyebrow">Atendimento online</span>
              <h3 className="font-serif text-3xl mt-4 text-[#2C352D]">
                Mesma escuta, qualquer lugar do mundo.
              </h3>
              <p className="mt-4 text-[#5B685C] font-light leading-relaxed">
                Sessões por videochamada em plataforma segura. Privacidade preservada, frequência semanal
                e a mesma profundidade do encontro presencial.
              </p>
              <Link data-testid="home-online-link" to="/psicanalise" className="mt-6 inline-flex items-center gap-2 text-[#8BA88E] hover:text-[#769379] font-medium">
                Saber mais <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section className="section">
        <div className="container-narrow">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <span className="text-eyebrow">Depoimentos</span>
              <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#2C352D] leading-tight">
                Palavras de quem encontrou um espaço de escuta.
              </h2>
            </div>
            <Link data-testid="home-leave-testimonial" to="/contato#depoimento" className="btn-outline self-start">
              Deixar meu depoimento
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.length === 0 && (
              <p data-testid="home-no-testimonials" className="text-[#5B685C] font-light italic col-span-2">
                Em breve, depoimentos autorizados serão exibidos aqui.
              </p>
            )}
            {testimonials.map((t) => (
              <article
                key={t.id}
                data-testid={`home-testimonial-${t.id}`}
                className="bg-[#F5F5ED] rounded-3xl p-8 md:p-10 border border-[#8BA88E]/10"
              >
                <Sparkles size={20} className="text-[#8BA88E]" />
                <p className="mt-4 font-serif text-2xl leading-snug text-[#2C352D] italic">
                  “{t.message}”
                </p>
                <p className="mt-6 text-eyebrow">— {t.name}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-[#F5F5ED]">
        <div className="container-narrow grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-eyebrow">Perguntas frequentes</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#2C352D] leading-tight">
              Dúvidas comuns sobre o processo.
            </h2>
            <p className="mt-6 text-[#5B685C] font-light">
              Não encontrou o que procurava? Me chame no WhatsApp — terei prazer em conversar.
            </p>
            <a
              data-testid="faq-whatsapp-btn"
              href={WHATSAPP_LINK("Olá Cristina, tenho uma dúvida.")}
              target="_blank"
              rel="noreferrer"
              className="btn-primary mt-8"
            >
              <MessageCircle size={16} /> Tirar dúvida
            </a>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqs.map((f, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  data-testid={`faq-item-${idx}`}
                  className="bg-white rounded-2xl border border-[#8BA88E]/10 px-6"
                >
                  <AccordionTrigger className="text-left text-[#2C352D] font-serif text-xl py-5 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5B685C] font-light leading-relaxed pb-5 text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-narrow">
          <div className="bg-gradient-to-br from-[#8BA88E] to-[#769379] rounded-[2.5rem] px-8 md:px-16 py-16 md:py-24 text-center relative overflow-hidden">
            <div aria-hidden className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
            <Heart size={28} className="text-white/80 mx-auto" />
            <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 max-w-3xl mx-auto leading-tight">
              Se algo em você pede escuta, eu te ouço.
            </h2>
            <p className="mt-6 text-white/85 font-light max-w-xl mx-auto">
              Marque uma conversa inicial. Sem compromisso, sem pressa — apenas a possibilidade de um começo.
            </p>
            <a
              data-testid="cta-whatsapp-btn"
              href={WHATSAPP_LINK("Olá Cristina, vim pelo site e gostaria de agendar.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-10 px-10 py-4 rounded-full bg-white text-[#2C352D] font-medium hover:scale-105 transition-transform shadow-xl"
            >
              <MessageCircle size={18} /> Agendar pelo WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
